import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Briefcase, Globe, Heart, Users } from "lucide-react";
import SEO, { pageSEO, schemas } from "../components/SEO";

export default function AboutPage() {
  const credentials = [
    {
      icon: Globe,
      title: "35+ Years in the U.S.",
      desc: "Deep understanding of what American travelers expect and appreciate"
    },
    {
      icon: Briefcase,
      title: "Corporate Background",
      desc: "Fortune 500 consulting and technology leadership — meticulous planning and execution"
    },
    {
      icon: Heart,
      title: "Indian Heritage",
      desc: "Born in India with family roots there"
    },
    {
      icon: Users,
      title: "Personal Touch",
      desc: "Treats every journey like hosting dear friends, not running a tour operation"
    }
  ];

  return (
    <div className="pt-24 md:pt-32" data-testid="about-page">
      <SEO {...pageSEO.about} />
      {/* Hero */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[#243b5a]" />
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.pexels.com/photos/30638768/pexels-photo-30638768.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="India Landscape"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 container-max px-6 md:px-12 lg:px-24 text-white text-center">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Your Host</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
            A Bridge Between <span className="text-[#D4AF37]">Two Worlds</span>
          </h1>
        </div>
      </section>

      {/* About Aaron */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center max-w-6xl mx-auto">
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-luxury">
                <img
                  src="https://customer-assets.emergentagent.com/job_taj-palaces-trip/artifacts/z8z5ik2b_AS_Pic_2025.jpg"
                  alt="Aaron - Your Host"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#D4AF37] text-white p-6 rounded-xl shadow-lg">
                <p className="font-serif text-2xl">Aaron</p>
                <p className="text-sm text-white/80">Your Host & Guide</p>
              </div>
            </div>

            {/* Content */}
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mb-8">
                Meet Your Host
              </h2>
              <div className="prose prose-lg text-[#4A4A4A] space-y-6">
                <p>
                  I was born in India but have called the United States home for over 35 years. My professional career took me through the worlds of technology and consulting — years at companies like Xerox and State Street Bank taught me the value of meticulous planning, attention to detail, and delivering experiences that exceed expectations.
                </p>
                <p>
                  But India never left my heart. I've returned, taking friends and colleagues on trips that opened their eyes to a country they thought they knew from headlines and stereotypes. Watching their wonder at the Taj at sunrise, their delight at discovering Indian hospitality, their peace in the Himalayan foothills — that became my passion.
                </p>
                <p>
                  Now, I'm channeling that passion into Awaken India Tours — carefully curated journeys for people who want to experience India's magic without the chaos, uncertainty, or compromise on comfort.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="section-padding bg-[#F9F9F7]" data-testid="credentials-section">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {credentials.map((cred, i) => (
              <div key={i} className="bg-white p-8 rounded-xl shadow-soft text-center" data-testid={`credential-${i}`}>
                <div className="w-16 h-16 rounded-full bg-[#243b5a]/10 flex items-center justify-center mx-auto mb-6">
                  <cred.icon className="w-7 h-7 text-[#243b5a]" />
                </div>
                <h3 className="font-serif text-xl text-[#243b5a] mb-3">{cred.title}</h3>
                <p className="text-[#4A4A4A] text-sm leading-relaxed">{cred.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="section-padding bg-[#243b5a] text-white">
        <div className="container-max text-center max-w-4xl mx-auto">
          <span className="quote-mark text-white/10">"</span>
          <blockquote className="font-serif text-2xl md:text-3xl italic leading-relaxed -mt-12">
            I treat every journey like I'm hosting dear friends, not running a tour operation. Your wonder is my reward.
          </blockquote>
          <p className="mt-8 text-[#D4AF37] font-medium">— Aaron</p>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-max text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mb-6">
            Ready to <span className="text-[#D4AF37]">Connect</span>?
          </h2>
          <p className="text-[#4A4A4A] text-lg max-w-2xl mx-auto mb-8">
            I'd love to hear about your travel dreams and answer any questions you might have.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="btn-gold" data-testid="schedule-call-btn">
                Schedule a Call
              </Button>
            </Link>
            <Link to="/faqs">
              <Button className="btn-secondary">
                Read FAQs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
